import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { StaffName } from "../components/StaffName";
import { GiveawayWheel } from "../components/GiveawayWheel";
import { FortuneWheel } from "../components/FortuneWheel";
import { SoundControls } from "../components/SoundControls";
import { playPop, initAudio, audioUnlocked } from "../lib/sounds";
import { openSocialShare } from "../lib/share";
import { toast } from "sonner";
import { Gift, Lock, Unlock, Users, ShieldCheck, Trophy, Radio, Share2, MessageCircle, Instagram, Music2, Link2, Copy, Volume2 } from "lucide-react";

// Logo ufficiale di X (lucide non include le icone brand)
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

function Countdown({ target, onDone }) {
  const { t } = useLang();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const done = diff <= 0;
  useEffect(() => {
    if (done && onDone) onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);
  const cells = [
    [t("events.days"), Math.floor(diff / 86400000)],
    [t("events.hours"), Math.floor((diff / 3600000) % 24)],
    [t("events.min"), Math.floor((diff / 60000) % 60)],
    [t("events.sec"), Math.floor((diff / 1000) % 60)],
  ];
  return (
    <div data-testid="giveaway-countdown">
      <p className="hud-label mb-3">{t("gv.starts_in")}</p>
      <div className="grid grid-cols-4 gap-px bg-[#1A1A1A] border border-[#1A1A1A] max-w-md">
        {cells.map(([label, v]) => (
          <div key={label} className="bg-[#050505] px-2 py-4 text-center">
            <p className="font-display text-3xl md:text-4xl text-wardogs leading-none">{String(v).padStart(2, "0")}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-steel mt-1">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShareGiveaway({ giveaway }) {
  const { t } = useLang();
  const url = `${window.location.origin}/giveaway`;
  const text = giveaway
    ? `${giveaway.title} — ${t("gv.prize")}: ${giveaway.prize} | WARDOGS.HQ`
    : `${t("gv.title")} | WARDOGS.HQ`;
  const full = `${text}\n${url}`;
  const openingMsg = (label) => t("share.opening", { social: label });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      toast.success(t("gv.share_copied"));
    } catch {
      toast.error("Copia non riuscita: seleziona e copia manualmente il link.");
    }
  };
  const native = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "WARDOGS.HQ — Giveaway", text, url });
      } catch { /* annullato dall'utente */ }
      return;
    }
    copy();
  };
  const open = (u) => window.open(u, "_blank", "noopener,noreferrer");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share.link_copied"));
    } catch {
      toast.error("Copia non riuscita: seleziona e copia manualmente il link.");
    }
  };

  const socials_list = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366", onClick: () => open(`https://wa.me/?text=${encodeURIComponent(full)}`) },
    { id: "x", label: "X", icon: XIcon, color: "#F5F5F5", onClick: () => open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(full)}`) },
    { id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C", onClick: () => openSocialShare("instagram", { full, openingMsg, siteUrl: url }) },
    { id: "tiktok", label: "TikTok", icon: Music2, color: "#FE2C55", onClick: () => openSocialShare("tiktok", { full, openingMsg, siteUrl: url }) },
    { id: "discord", label: "Discord", icon: Link2, color: "#5865F2", onClick: () => openSocialShare("discord", { full, openingMsg }) },
    { id: "copylink", label: t("share.copy_link"), icon: Copy, color: "#C9C9CE", onClick: copyLink },
  ];

  return (
    <Reveal delay={0.12}>
      <div className="mt-10 chamfer border border-[#1A1A1A] bg-[#050505] p-8" data-testid="giveaway-share">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <p className="font-display text-3xl uppercase mb-1">{t("gv.share")}</p>
            <p className="text-sm text-steel">{t("gv.share_sub")}</p>
            <p className="font-mono text-[10px] text-steel mt-2">{t("gv.share_note")}</p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button onClick={native} data-testid="giveaway-share-button" className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300 flex items-center gap-2">
              <Share2 className="w-4 h-4" /> {t("gv.share")}
            </button>
            {socials_list.map((s) => (
              <button
                key={s.id}
                onClick={s.onClick}
                data-testid={`share-${s.id}`}
                title={s.label}
                aria-label={`Condividi su ${s.label}`}
                className="chamfer-sm border border-[#2A2A2A] hover:bg-[#0C0C0C] font-mono text-[11px] uppercase tracking-[0.15em] font-bold px-4 py-3.5 transition-colors duration-300 flex items-center gap-2"
                style={{ color: s.color }}
              >
                <s.icon className="w-4 h-4" /> {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Giveaway() {
  const { user, login, twitchConfigured } = useAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [entering, setEntering] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [wheelDone, setWheelDone] = useState(false);
  const [newJoiner, setNewJoiner] = useState(null);
  const prevNames = useRef(null);
  const soundOnRef = useRef(true);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    soundOnRef.current = next;
    if (next) playPop(); // prova immediata: conferma che i suoni si sentono
  };

  // I browser sbloccano l'audio solo dopo un'interazione: riprovo a OGNI gesto
  // finche' il contesto non parte (resume e' asincrono, un solo tentativo puo' fallire)
  useEffect(() => {
    const unlock = () => {
      initAudio();
      setTimeout(() => { if (audioUnlocked()) setAudioReady(true); }, 200);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);


  const load = () => api.get("/giveaways").then(({ data }) => {
    setData(data);
    // nuova iscrizione mentre la pagina e' aperta -> sparkle + nome in grande
    const names = (data?.entries_list || []).map((e) => e.login);
    if (prevNames.current !== null) {
      const seen = new Set(prevNames.current);
      const added = names.filter((l) => !seen.has(l));
      if (added.length) {
        if (soundOnRef.current) playPop();
        setNewJoiner(added[added.length - 1]);
      }
    }
    prevNames.current = names;
  }).catch(() => {});

  // il banner del nuovo iscritto resta in scena pochi secondi
  useEffect(() => {
    if (!newJoiner) return undefined;
    const tm = setTimeout(() => setNewJoiner(null), 4500);
    return () => clearTimeout(tm);
  }, [newJoiner]);
  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const giveaway = data?.active;
  const drawing = Boolean(giveaway?.drawing && giveaway?.winner);
  // azzera lo stato ruota quando cambia il giveaway
  useEffect(() => { setWheelDone(false); }, [giveaway?.id]);

  // all'avvio dell'estrazione: tentativo AUTOMATICO di attivare l'audio — parte
  // da solo se il browser consente l'autoplay (utente che ha gia' interagito col
  // sito, oppure OBS Browser Source). Altrimenti compare l'overlay di sblocco.
  useEffect(() => {
    if (!drawing) return undefined;
    initAudio();
    const iv = setInterval(() => {
      initAudio();
      if (audioUnlocked()) { setAudioReady(true); clearInterval(iv); }
    }, 1500);
    const tm = setTimeout(() => clearInterval(iv), 15000);
    return () => { clearInterval(iv); clearTimeout(tm); };
  }, [drawing]);

  const enter = async () => {
    setEntering(true);
    try {
      const { data: res } = await api.post("/giveaway/enter");
      if (res.already) toast.info(t("gv.toast_already"));
      else toast.success(t("gv.toast_in"));
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "not_subscribed") toast.error(t("gv.toast_notsub"));
      else if (detail === "relogin_twitch") toast.error(t("gv.toast_relogin"));
      else if (detail === "not_open_yet") toast.error(t("gv.toast_notopen"));
      else toast.error(t("gv.toast_error"));
    } finally {
      setEntering(false);
    }
  };

  const isOpen = giveaway ? giveaway.open !== false : false;
  const startsAt = giveaway && !isOpen ? giveaway.scheduled_at : null;
  const past = data?.past || [];

  return (
    <main className="pt-32 pb-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("gv.over")}</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-4">
            {t("gv.title").split(" ").slice(0, -1).join(" ")} <span className="text-wardogs">{t("gv.title").split(" ").slice(-1)}</span>
          </h1>
          <p className="text-steel text-base md:text-lg max-w-xl mb-6">
            {t("gv.text")}
          </p>
          <div className="chamfer-sm border border-wardogs/30 bg-wardogs-dim px-5 py-4 inline-flex items-center gap-3 mb-14" data-testid="sub-detection-info">
            <Radio className="w-4 h-4 text-wardogs shrink-0" />
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel">
              {t("gv.subinfo")}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {drawing && !wheelDone ? (
            <GiveawayWheel
              names={(data.entries_list || []).map((e) => e.login)}
              winner={giveaway.winner.login}
              startedAt={giveaway.draw_started_at}
              durationMs={30000}
              soundOn={soundOn}
              onToggleSound={toggleSound}
              onDone={() => { setWheelDone(true); setTimeout(load, 3000); }}
            />
          ) : (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] relative overflow-hidden" data-testid="giveaway-vault">
            <div className="absolute top-0 left-0 right-0 h-px bg-wardogs/60" />
            <div className="grid lg:grid-cols-[1fr_400px]">
              <div className="p-8 md:p-12 scanlines-bg">
                <div className="flex items-center gap-3 mb-6">
                  <Gift className="w-6 h-6 text-wardogs" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-wardogs">
                    {giveaway ? (isOpen ? t("gv.active") : t("gv.scheduled_badge")) : t("gv.soon")}
                  </span>
                  <div className="ml-auto">
                    <SoundControls soundOn={soundOn} onToggle={toggleSound} />
                  </div>
                </div>
                {giveaway ? (
                  <>
                    <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] mb-5" data-testid="giveaway-title">
                      {giveaway.title}
                    </h2>
                    <p className="text-steel text-base max-w-lg mb-8">{giveaway.description}</p>
                    <div className="flex flex-wrap items-center gap-6">
                      <div className="chamfer-sm border border-wardogs/40 bg-wardogs-dim px-6 py-3">
                        <p className="hud-label mb-1">{t("gv.prize")}</p>
                        <p className="font-display text-2xl uppercase text-wardogs" data-testid="giveaway-prize">{giveaway.prize}</p>
                      </div>
                      <div className="flex items-center gap-2 font-mono text-xs text-steel" data-testid="giveaway-entries-count">
                        <Users className="w-4 h-4 text-wardogs" />
                        {t("gv.entries", { n: data?.entries ?? 0 })}
                      </div>
                    </div>
                    {startsAt && (
                      <div className="mt-8">
                        <Countdown target={startsAt} onDone={load} />
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-steel text-base" data-testid="giveaway-none">{t("gv.none")}</p>
                )}
              </div>

              <div className="border-t lg:border-t-0 lg:border-l border-[#1A1A1A] p-8 md:p-10 flex flex-col justify-center gap-6 bg-[#050505]">
                {giveaway && !isOpen ? (
                  <>
                    <Lock className="w-8 h-8 text-wardogs" />
                    <p className="font-display text-2xl uppercase">{t("gv.scheduled_badge")}</p>
                    <p className="text-sm text-steel" data-testid="giveaway-not-open">{t("gv.not_open")}</p>
                  </>
                ) : !user ? (
                  <>
                    <Lock className="w-8 h-8 text-valkyra" />
                    <p className="font-display text-2xl uppercase">{t("gv.login_title")}</p>
                    <p className="text-sm text-steel">{t("gv.login_text")}</p>
                    <button
                      onClick={login}
                      disabled={!twitchConfigured}
                      data-testid="giveaway-login-button"
                      className="chamfer-sm bg-[#9146FF] hover:bg-[#7d3aed] disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300"
                    >
                      {t("nav.login_twitch")}
                    </button>
                  </>
                ) : data?.entered ? (                  <>
                    <ShieldCheck className="w-8 h-8 text-manticore" />
                    <p className="font-display text-2xl uppercase text-manticore">{t("gv.in_title")}</p>
                    <p className="text-sm text-steel">{t("gv.in_text", { name: user.display_name })}</p>
                    <div className="chamfer-sm border border-manticore/40 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-manticore" data-testid="giveaway-entered-badge">
                      <Unlock className="w-4 h-4 inline mr-2" /> {t("gv.unlocked")}
                    </div>
                  </>
                ) : giveaway && user.provider && user.provider !== "twitch" ? (
                  <>
                    <Lock className="w-8 h-8 text-wardogs" />
                    <p className="font-display text-2xl uppercase">{t("gv.subs_title")}</p>
                    <p className="text-sm text-steel" data-testid="giveaway-twitch-required">{t("gv.twitch_required")}</p>
                  </>
                ) : giveaway ? (
                  <>
                    <Lock className="w-8 h-8 text-wardogs" />
                    <p className="font-display text-2xl uppercase">{t("gv.subs_title")}</p>
                    <p className="text-sm text-steel">{t("gv.subs_text")}</p>
                    <button
                      onClick={enter}
                      disabled={entering || !twitchConfigured}
                      data-testid="giveaway-enter-button"
                      className="chamfer-sm bg-wardogs hover:bg-wardogs-hover disabled:opacity-50 disabled:cursor-not-allowed text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300"
                    >
                      {entering ? t("gv.checking") : t("gv.enter")}
                    </button>
                    <a
                      href="https://www.twitch.tv/subs/ilquartiermastro"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="giveaway-subscribe-link"
                      className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-steel hover:text-wardogs transition-colors duration-300"
                    >
                      {t("gv.sub_link")}
                    </a>
                  </>
                ) : (
                  <>
                    <Gift className="w-8 h-8 text-steel" />
                    <p className="text-sm text-steel">{t("gv.none")}</p>
                  </>
                )}
              </div>
            </div>
          </div>
          )}
        </Reveal>

        <ShareGiveaway giveaway={giveaway} />

      {newJoiner && (
        <div key={newJoiner} className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 chamfer-sm border border-manticore/50 bg-[#050505]/95 px-6 py-4 text-center" data-testid="giveaway-new-joiner">
          <style>{`@keyframes gw-join { 0% { transform: translate(-50%, 12px) scale(0.9); opacity: 0; } 100% { transform: translate(-50%, 0) scale(1); opacity: 1; } }`}</style>
          <div style={{ animation: "gw-join 0.35s cubic-bezier(0.2, 1.4, 0.4, 1) both" }}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-manticore mb-1">{t("gv.joined")}</p>
            <p className="font-display text-2xl uppercase text-white leading-none">{newJoiner}</p>
          </div>
        </div>
      )}

      {/* durante il giro, se l'audio e' ancora bloccato dal browser: overlay a
          tutto schermo impossibile da mancare — UN click e senti il resto del giro */}
      {drawing && !wheelDone && !audioReady && (
        <button
          type="button"
          onClick={() => { initAudio(); setTimeout(() => { if (audioUnlocked()) setAudioReady(true); }, 200); }}
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-5 bg-[#050505]/60 cursor-pointer"
          data-testid="giveaway-audio-overlay"
        >
          <span className="chamfer border border-wardogs bg-wardogs-dim px-10 py-6 flex items-center gap-4 animate-pulse pointer-events-none">
            <Volume2 className="w-6 h-6 text-wardogs" />
            <span className="font-display text-2xl uppercase text-white">{t("gv.audio_overlay")}</span>
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setAudioReady(true); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setAudioReady(true); } }}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel hover:text-white transition-colors duration-300 underline underline-offset-4"
            data-testid="giveaway-audio-skip"
          >
            {t("gv.audio_skip")}
          </span>
        </button>
      )}

      {!audioReady && (
        <div className="fixed bottom-5 right-5 z-50 chamfer-sm border border-wardogs/50 bg-[#050505]/95 px-4 py-3 flex items-center gap-3 animate-pulse" data-testid="giveaway-audio-hint">
          <Volume2 className="w-4 h-4 text-wardogs" />
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-steel">{t("gv.audio_hint")}</p>
        </div>
      )}

        {giveaway && !drawing && (
          <Reveal delay={0.12}>
            <p className="hud-label mt-16 mb-6">{t("gv.participants")} — {data?.entries ?? 0}</p>
            {(data?.entries_list || []).length === 0 ? (
              <div className="chamfer border border-[#1A1A1A] bg-[#050505] p-8" data-testid="giveaway-participants-empty">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A4D51]">{t("gv.no_participants")}</p>
              </div>
            ) : (
              <div className="chamfer border border-[#1A1A1A] bg-[#050505] p-8 md:p-10" data-testid="giveaway-participants">
                <FortuneWheel names={data.entries_list.map((e) => e.login)} size={640} />
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A4D51] mt-6 text-center">{t("gv.wheel_ready")}</p>
                <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A] mt-8 max-h-72 overflow-y-auto" data-lenis-prevent>
                  {data.entries_list.map((e, i) => (
                    <div key={`${e.login}-${i}`} className="bg-[#050505] px-5 py-3.5 flex items-center gap-4" data-testid={`giveaway-entry-${e.login}`}>
                      <span className="font-mono text-[10px] text-[#4A4D51] w-8">{String(i + 1).padStart(2, "0")}</span>
                      {e.is_staff ? (
                        <StaffName name={e.login} className="font-mono text-xs flex-1 truncate" />
                      ) : (
                        <span className="font-mono text-xs text-white flex-1 truncate">{e.login}</span>
                      )}
                      {e.tier && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9146FF]">
                          T{String(e.tier)[0]}
                        </span>
                      )}
                      <span className="font-mono text-[10px] text-steel">
                        {new Date(e.entered_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        )}

        {past.length > 0 && (
          <Reveal delay={0.12}>
            <p className="hud-label mt-16 mb-6">{t("gv.hof")}</p>
            <div className="grid gap-px bg-[#1A1A1A] border border-[#1A1A1A] sm:grid-cols-2 lg:grid-cols-3">
              {past.map((g, i) => (
                <div key={g.id} className="bg-[#050505] p-6 scanlines-bg relative flex flex-col items-center text-center" data-testid={`giveaway-past-${g.id}`}>
                  {g.winner ? (
                    <>
                      {i === 0 && (
                        <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.2em] text-manticore border border-manticore/40 px-2 py-1">{t("gv.hof_last")}</span>
                      )}
                      <img
                        src={g.winner.avatar || "/logo-wolf.png"}
                        alt={`Avatar di ${g.winner.login}`}
                        className="w-16 h-16 rounded-full border-2 border-manticore/60 object-cover mb-4"
                        loading="lazy"
                        data-testid={`hof-avatar-${g.id}`}
                      />
                      <p className="font-display text-2xl uppercase text-white leading-none mb-1.5 truncate max-w-full">{g.winner.login}</p>
                      <p className="font-mono text-[11px] text-wardogs mb-1 truncate max-w-full">{g.prize}</p>
                      <p className="font-mono text-[10px] text-steel truncate max-w-full">{g.title}</p>
                      <p className="font-mono text-[10px] text-[#4A4D51] mt-1">
                        {new Date(g.closed_at || g.winner.drawn_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                      </p>
                    </>
                  ) : (
                    <>
                      <Trophy className="w-5 h-5 text-[#4A4D51] mb-4" />
                      <p className="font-display text-lg uppercase text-steel leading-none mb-1.5 truncate max-w-full">{g.title}</p>
                      <p className="font-mono text-[10px] text-[#4A4D51]">{t("gv.no_winner")}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <div className="mt-10 grid gap-px bg-[#1A1A1A] border border-[#1A1A1A] md:grid-cols-3">
            {[["01", t("gv.how1t"), t("gv.how1x")], ["02", t("gv.how2t"), t("gv.how2x")], ["03", t("gv.how3t"), t("gv.how3x")]].map(([num, title, text]) => (
              <div key={num} className="bg-[#050505] p-8">
                <p className="font-display text-4xl text-wardogs/40 mb-3">{num}</p>
                <p className="font-display text-2xl uppercase mb-2">{title}</p>
                <p className="text-sm text-steel">{text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
